import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, MessageCircle, Star, ArrowLeft, CheckCircle2, MapPin, ArrowRight } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { FloatingWhatsApp } from './FloatingWhatsApp';
import { useCanonical } from '../hooks/useCanonical';

const servicesData = {
  'retour-affectif': {
    title: 'Retour Affectif Rapide à Genève',
    subtitle: 'Faire Revenir l\'Être Aimé en 3 à 7 Jours',
    metaTitle: 'Retour Affectif Genève | Marabout Retour Être Aimé Rapide',
    metaDesc: 'Retour affectif rapide à Genève en 3-7 jours. Marabout africain sérieux. 800+ clients satisfaits. Consultation gratuite 7j/7.',
    intro: 'Vous souffrez d\'une rupture amoureuse ? Votre conjoint(e) vous a quitté(e) ? Notre grand marabout africain à Genève est le spécialiste N°1 du retour affectif rapide. Grâce à des rituels ancestraux puissants transmis de génération en génération, il peut faire revenir l\'être aimé en seulement 3 à 7 jours.',
    sections: [
      { title: 'Comment Fonctionne le Retour Affectif ?', content: 'Le retour affectif est un travail spirituel qui agit sur les sentiments et les énergies entre deux personnes. Notre marabout à Genève utilise des rituels de magie blanche ancestraux pour raviver la flamme et provoquer le retour de l\'être cher. Ces rituels sont sûrs, sans danger et respectueux du libre arbitre.' },
      { title: 'Résultats Garantis en 3 à 7 Jours', content: 'Contrairement aux charlatans, notre marabout sérieux à Genève garantit des résultats concrets en 3 à 7 jours maximum. Des centaines de couples ont été réunis grâce à son travail. Que votre ex vous ait quitté(e) pour une autre personne, qu\'il y ait eu une dispute violente ou une séparation longue, les rituels fonctionnent.' },
      { title: 'Réconciliation Amoureuse et Retour de l\'Ex', content: 'Que vous souhaitiez récupérer votre ex, sauver votre mariage, ou renforcer les liens de votre couple, notre marabout propose des solutions adaptées : retour de l\'ex, réconciliation amoureuse, renforcement des sentiments, éloignement d\'un(e) rival(e), fidélité dans le couple.' },
      { title: 'Discrétion et Confidentialité', content: 'Toutes les consultations sont strictement confidentielles. Notre marabout à Genève garantit une discrétion absolue. Votre situation reste entre vous et lui. Facilité de paiement disponible pour vous accompagner.' },
    ],
    keywords: ['retour affectif genève', 'faire revenir son ex', 'retour être aimé', 'réconciliation amoureuse', 'marabout retour affectif', 'retour affectif rapide'],
    testimonials: [
      { name: 'Sophie M.', city: 'Genève', text: 'Mon mari est revenu en 5 jours ! Incroyable.' },
      { name: 'Amina B.', city: 'Carouge', text: 'Mon ex m\'a suppliée de le reprendre après 4 jours.' },
      { name: 'David S.', city: 'Lausanne', text: 'Ma femme est revenue après un an de séparation.' },
    ],
  },
  'voyance': {
    title: 'Voyance et Médiumnité à Genève',
    subtitle: 'Voyant Médium Africain Sérieux et Reconnu',
    metaTitle: 'Voyant Genève | Médium Africain Sérieux | Voyance Genève',
    metaDesc: 'Voyant médium sérieux à Genève. Consultation de voyance précise. 30 ans d\'expérience. Prédictions fiables. Consultation gratuite.',
    intro: 'Vous cherchez un voyant sérieux à Genève ? Notre grand marabout africain possède un don exceptionnel de clairvoyance hérité de ses ancêtres. Ses consultations de voyance sont reconnues pour leur précision remarquable. Que ce soit pour l\'amour, le travail, la santé ou les finances, il éclaire votre avenir.',
    sections: [
      { title: 'Voyance Précise et Fiable', content: 'Notre voyant médium à Genève utilise différentes techniques de divination héritées de la tradition africaine : consultation des cauris, lecture des lignes de la main, voyance par les étoiles. Chaque consultation est unique et personnalisée.' },
      { title: 'Don de Médiumnité Exceptionnel', content: 'En tant que médium reconnu à Genève, notre marabout peut communiquer avec le monde spirituel. Il perçoit les énergies invisibles et reçoit des messages qui vous guident dans vos décisions importantes.' },
      { title: 'Consultation en Cabinet ou à Distance', content: 'Les consultations de voyance sont disponibles en personne dans notre cabinet de Genève ou par téléphone/WhatsApp. La distance n\'affecte en rien la qualité de la voyance.' },
      { title: 'Domaines de Consultation', content: 'Amour et couple, carrière et travail, santé et bien-être, finances et affaires, famille et relations, projets et décisions importantes. Notre voyant à Genève vous éclaire sur tous les aspects de votre vie.' },
    ],
    keywords: ['voyant genève', 'médium genève', 'voyance genève', 'consultation voyance', 'voyant médium sérieux', 'voyant africain genève'],
    testimonials: [
      { name: 'Fatima K.', city: 'Genève', text: 'Tout ce qu\'il m\'a dit s\'est réalisé. Précision incroyable.' },
      { name: 'Jean-Marc F.', city: 'Nyon', text: 'Des prédictions qui se sont réalisées au jour près.' },
      { name: 'Valérie F.', city: 'Neuchâtel', text: 'La meilleure voyance que j\'ai expérimentée.' },
    ],
  },
  'desenvoutement': {
    title: 'Désenvoûtement Puissant à Genève',
    subtitle: 'Libérez-vous des Mauvais Sorts et Malédictions',
    metaTitle: 'Désenvoûtement Genève | Marabout Levée de Sort Puissant',
    metaDesc: 'Désenvoûtement puissant à Genève. Expert levée de sorts et malédictions. Protection spirituelle durable. 30 ans d\'expérience. Consultation gratuite.',
    intro: 'Vous vous sentez victime d\'un envoûtement ou d\'un mauvais sort ? Malchance persistante, cauchemars, conflits inexpliqués ? Notre grand marabout africain à Genève est expert en désenvoûtement puissant. Il identifie la source du problème et vous en libère définitivement.',
    sections: [
      { title: 'Signes d\'un Envoûtement', content: 'Les signes incluent : enchaînement de malchances, cauchemars récurrents, conflits permanents, échecs répétés, problèmes de santé sans cause médicale, fatigue inexpliquée, dépression soudaine, perte de motivation. Si vous reconnaissez plusieurs de ces signes, vous êtes peut-être victime d\'un envoûtement.' },
      { title: 'Désenvoûtement Rapide et Efficace', content: 'Notre marabout à Genève utilise des rituels de désenvoûtement ancestraux pour identifier et neutraliser tout type d\'envoûtement : magie noire, mauvais oeil, jalousie, malédiction familiale, sort amoureux. Le désenvoûtement agit rapidement et définitivement.' },
      { title: 'Protection Spirituelle Durable', content: 'Après le désenvoûtement, notre marabout met en place une protection spirituelle puissante pour vous protéger contre de futurs envoûtements. Cette protection agit comme un bouclier invisible qui repousse les énergies négatives.' },
      { title: 'Purification Complète', content: 'En plus du désenvoûtement personnel, notre marabout peut purifier votre maison, votre lieu de travail et vos objets personnels. La purification complète élimine toute trace d\'énergie négative de votre environnement.' },
    ],
    keywords: ['désenvoûtement genève', 'protection spirituelle', 'levée de sort', 'mauvais oeil', 'envoûtement', 'marabout désenvoûtement'],
    testimonials: [
      { name: 'Pierre D.', city: 'Lausanne', text: 'Libéré d\'un envoûtement de plusieurs années. Ma vie a changé.' },
      { name: 'Mohamed A.', city: 'Versoix', text: 'Le désenvoûtement m\'a permis de revivre normalement.' },
      { name: 'Claire N.', city: 'Genève', text: 'La protection spirituelle m\'a sauvée. Je vis sereinement.' },
    ],
  },
  'protection-spirituelle': {
    title: 'Protection Spirituelle à Genève',
    subtitle: 'Protégez-vous Contre les Forces Négatives',
    metaTitle: 'Protection Spirituelle Genève | Marabout Contre Mauvais Sort',
    metaDesc: 'Protection spirituelle puissante à Genève. Contre mauvais oeil, jalousie, forces négatives. Marabout africain sérieux. Consultation gratuite 7j/7.',
    intro: 'La protection spirituelle est essentielle pour se prémunir contre les énergies négatives, la jalousie et les mauvais sorts. Notre grand marabout africain à Genève offre une protection spirituelle puissante et durable qui agit comme un bouclier invisible.',
    sections: [
      { title: 'Types de Protection Spirituelle', content: 'Notre marabout propose différents types de protection : protection contre le mauvais oeil, protection contre la jalousie, protection du foyer, protection du couple, protection du travail, protection des enfants. Chaque protection est adaptée à votre situation.' },
      { title: 'Comment Fonctionne la Protection ?', content: 'La protection spirituelle utilise des rituels ancestraux qui créent un bouclier énergétique autour de vous. Ce bouclier repousse les énergies négatives et les tentatives d\'envoûtement. La protection est renforcée régulièrement pour rester efficace.' },
      { title: 'Protection du Foyer et de la Famille', content: 'Votre maison est votre sanctuaire. Notre marabout à Genève peut protéger votre foyer et votre famille contre les influences néfastes. La purification et la protection de votre domicile apportent harmonie et sérénité.' },
      { title: 'Protection Professionnelle', content: 'Protégez votre carrière et vos affaires contre la jalousie et les sabotages. Notre marabout offre une protection efficace pour votre réussite professionnelle et vos projets d\'entreprise.' },
    ],
    keywords: ['protection spirituelle genève', 'protection mauvais oeil', 'protection contre jalousie', 'marabout protection', 'bouclier spirituel'],
    testimonials: [
      { name: 'Marie-Claire R.', city: 'Meyrin', text: 'Protection contre ma belle-famille. Tout va mieux.' },
      { name: 'Sylvie T.', city: 'Genève', text: 'J\'ai senti la différence dès le premier jour.' },
      { name: 'Claire N.', city: 'Genève', text: 'La protection est puissante et durable.' },
    ],
  },
  'chance-aux-jeux': {
    title: 'Chance aux Jeux à Genève',
    subtitle: 'Rituels Puissants pour Attirer la Fortune',
    metaTitle: 'Chance aux Jeux Genève | Rituel Fortune | Marabout Chance',
    metaDesc: 'Rituels de chance aux jeux à Genève. Attirez fortune et prospérité. Marabout africain spécialiste. Résultats rapides. Consultation gratuite.',
    intro: 'Vous rêvez de gagner aux jeux ? Notre grand marabout africain à Genève possède des rituels ancestraux puissants pour attirer la chance et la fortune dans votre vie. Des dizaines de clients ont vu leur situation financière se transformer grâce à ces rituels.',
    sections: [
      { title: 'Rituels de Chance Puissants', content: 'Notre marabout utilise des rituels ancestraux africains spécialement conçus pour attirer la chance et la fortune. Ces rituels agissent sur votre énergie personnelle pour ouvrir les portes de la prospérité.' },
      { title: 'Attraction de la Prospérité', content: 'Au-delà des jeux, nos rituels attirent la prospérité dans tous les domaines : finances, carrière, investissements. Vous remarquerez des opportunités qui se présentent naturellement.' },
      { title: 'Déblocage Financier', content: 'Si vous traversez une période de difficultés financières, notre marabout peut débloquer votre situation. Les rituels de déblocage lèvent les obstacles invisibles qui freinent votre réussite financière.' },
      { title: 'Résultats Concrets', content: 'Nos clients témoignent de gains significatifs après les rituels. La chance n\'est pas le hasard quand on sait comment l\'attirer. Notre marabout à Genève vous guide vers la prospérité.' },
    ],
    keywords: ['chance aux jeux genève', 'rituel chance', 'attirer fortune', 'marabout chance', 'gagner aux jeux', 'prospérité'],
    testimonials: [
      { name: 'Thomas L.', city: 'Vernier', text: 'Gains significatifs après les rituels. Situation financière transformée.' },
      { name: 'Caroline G.', city: 'Montreux', text: 'Des opportunités incroyables se sont présentées.' },
      { name: 'Bernard J.', city: 'Berne', text: 'Les rituels de chance ont changé ma vie financière.' },
    ],
  },
  'guerison-spirituelle': {
    title: 'Guérison Spirituelle à Genève',
    subtitle: 'Soins Énergétiques et Guérison par les Rituels Ancestraux',
    metaTitle: 'Guérison Spirituelle Genève | Guérisseur Africain Sérieux',
    metaDesc: 'Guérison spirituelle à Genève. Soins énergétiques par un marabout guérisseur africain. 30 ans d\'expérience. Consultation gratuite.',
    intro: 'La guérison spirituelle est une approche ancestrale qui traite les maux à leur source énergétique. Notre grand marabout africain à Genève, héritier d\'une longue lignée de guérisseurs, utilise des techniques de soins transmises de génération en génération pour restaurer votre bien-être.',
    sections: [
      { title: 'Soins Énergétiques Traditionnels', content: 'Notre guérisseur à Genève utilise des techniques ancestrales africaines : imposition des mains, purification par les plantes, bains spirituels, prières de guérison. Ces soins agissent sur le corps, l\'esprit et l\'âme.' },
      { title: 'Guérison des Maux Inexpliqués', content: 'Certains maux n\'ont pas de cause médicale identifiable. Fatigue chronique, douleurs persistantes, insomnie, anxiété sans raison. Notre marabout identifie la cause spirituelle et apporte la guérison.' },
      { title: 'Purification et Renouveau', content: 'La purification spirituelle élimine les énergies négatives accumulées. Après un bain spirituel ou une séance de purification, vous ressentez un soulagement immédiat et un regain d\'énergie.' },
      { title: 'Approche Holistique', content: 'Notre marabout traite la personne dans sa globalité. Corps, esprit et âme sont interconnectés. La guérison spirituelle restaure l\'équilibre entre ces trois dimensions pour un bien-être durable.' },
    ],
    keywords: ['guérison spirituelle genève', 'guérisseur genève', 'soins énergétiques', 'guérisseur africain', 'purification spirituelle'],
    testimonials: [
      { name: 'Youssef H.', city: 'Lausanne', text: 'J\'ai retrouvé ma santé et mon énergie. Un vrai miracle.' },
      { name: 'Nathalie V.', city: 'Genève', text: 'Les soins m\'ont transformée. Je revis enfin.' },
      { name: 'François R.', city: 'Genève', text: 'Guérison complète après des mois de souffrance.' },
    ],
  },
  'problemes-famille': {
    title: 'Résolution de Problèmes de Famille à Genève',
    subtitle: 'Retrouvez l\'Harmonie Familiale',
    metaTitle: 'Problèmes Famille Genève | Réconciliation | Marabout Famille',
    metaDesc: 'Résolution de problèmes de famille à Genève. Réconciliation familiale, conflits. Marabout africain spécialiste. Consultation gratuite.',
    intro: 'Les conflits familiaux peuvent détruire l\'harmonie de votre foyer. Notre grand marabout africain à Genève est spécialisé dans la résolution des problèmes de famille. Grâce à des rituels de réconciliation puissants, il rétablit la paix et l\'harmonie dans votre foyer.',
    sections: [
      { title: 'Conflits Familiaux', content: 'Disputes entre époux, conflits avec la belle-famille, tensions entre parents et enfants, rivalités fraternelles. Notre marabout identifie la source des conflits et apporte des solutions durables.' },
      { title: 'Réconciliation Familiale', content: 'Les rituels de réconciliation agissent sur les énergies entre les membres de la famille. Ils favorisent le pardon, la compréhension et le rapprochement. L\'harmonie familiale est restaurée durablement.' },
      { title: 'Protection du Foyer', content: 'Après la réconciliation, notre marabout met en place une protection du foyer pour maintenir la paix. Cette protection empêche les influences extérieures de perturber votre vie familiale.' },
      { title: 'Problèmes de Couple', content: 'Infidélité, manque de communication, perte de passion, conflits répétés. Notre marabout à Genève aide les couples à surmonter toutes les difficultés et à retrouver le bonheur conjugal.' },
    ],
    keywords: ['problèmes famille genève', 'réconciliation familiale', 'conflits familiaux', 'marabout famille', 'harmonie familiale'],
    testimonials: [
      { name: 'Aïcha D.', city: 'Onex', text: 'Les conflits familiaux ont cessé. Nous vivons en paix.' },
      { name: 'Mariam C.', city: 'Lancy', text: 'Problèmes qui empoisonnaient ma vie enfin résolus.' },
      { name: 'Christine L.', city: 'Genève', text: 'Ma belle-mère a cessé de s\'opposer à notre mariage.' },
    ],
  },
  'reussite-professionnelle': {
    title: 'Réussite Professionnelle à Genève',
    subtitle: 'Débloquez Votre Carrière et Attirez le Succès',
    metaTitle: 'Réussite Professionnelle Genève | Marabout Succès Travail',
    metaDesc: 'Réussite professionnelle à Genève. Déblocage carrière, réussite examens, succès affaires. Marabout africain sérieux. Consultation gratuite.',
    intro: 'Votre carrière stagne ? Vous échouez aux examens malgré vos efforts ? Votre entreprise ne décolle pas ? Notre grand marabout africain à Genève possède des rituels puissants pour débloquer votre situation professionnelle et attirer le succès.',
    sections: [
      { title: 'Déblocage Professionnel', content: 'Parfois, des blocages invisibles empêchent votre progression. Notre marabout identifie et lève ces obstacles : jalousie de collègues, mauvais oeil, malédiction professionnelle. Après le déblocage, les opportunités affluent.' },
      { title: 'Réussite aux Examens', content: 'Bac, permis de conduire, concours, examens universitaires. Nos rituels de réussite renforcent votre confiance, votre concentration et votre mémoire. Des dizaines de clients ont réussi grâce à ces rituels.' },
      { title: 'Succès en Affaires', content: 'Entrepreneurs, commerçants, indépendants : nos rituels attirent les clients, favorisent les contrats et boostent votre chiffre d\'affaires. Votre entreprise prospère grâce à l\'énergie positive.' },
      { title: 'Recherche d\'Emploi', content: 'Vous cherchez un emploi depuis longtemps ? Notre marabout lève les obstacles qui bloquent votre recherche et attire les opportunités professionnelles. Des clients ont retrouvé un emploi en quelques semaines.' },
    ],
    keywords: ['réussite professionnelle genève', 'déblocage carrière', 'réussite examens', 'succès affaires', 'marabout travail'],
    testimonials: [
      { name: 'Hassan O.', city: 'Lancy', text: 'Permis de conduire obtenu après 3 échecs.' },
      { name: 'Laurent B.', city: 'Genève', text: 'Emploi retrouvé en 2 semaines grâce aux rituels.' },
      { name: 'Patrick E.', city: 'Genève', text: 'Promu au travail après les rituels de réussite.' },
    ],
  },
};

const otherServices = [
  { slug: 'retour-affectif', name: 'Retour Affectif' },
  { slug: 'voyance', name: 'Voyance' },
  { slug: 'desenvoutement', name: 'Désenvoûtement' },
  { slug: 'protection-spirituelle', name: 'Protection' },
  { slug: 'chance-aux-jeux', name: 'Chance aux Jeux' },
  { slug: 'guerison-spirituelle', name: 'Guérison' },
  { slug: 'problemes-famille', name: 'Famille' },
  { slug: 'reussite-professionnelle', name: 'Réussite' },
];

export const ServicePage = () => {
  const { slug } = useParams();
  const service = servicesData[slug];
  useCanonical(`/service/${slug}`);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) {
      document.title = service.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', service.metaDesc);

      const existingSchema = document.getElementById('service-schema');
      if (existingSchema) existingSchema.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'service-schema';
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": service.title,
        "description": service.metaDesc,
        "provider": {
          "@type": "LocalBusiness",
          "name": "Marabout Genève - Grand Voyant Médium Africain",
          "telephone": "+41767836186",
          "address": { "@type": "PostalAddress", "addressLocality": "Genève", "addressRegion": "GE", "postalCode": "1201", "addressCountry": "CH" }
        },
        "areaServed": { "@type": "City", "name": "Genève" },
        "url": `https://marabout-geneve.ch/service/${slug}`
      });
      document.head.appendChild(script);
      return () => { const el = document.getElementById('service-schema'); if (el) el.remove(); };
    }
  }, [slug, service]);

  if (!service) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-amber-400 mb-4">Service Non Trouvé</h1>
          <Link to="/" className="text-amber-400 hover:text-amber-300">← Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      {/* Hero */}
      <section className="pt-24 pb-12 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgb(212, 168, 67) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/" className="inline-flex items-center text-amber-400 hover:text-amber-300 mb-6 transition-colors" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4 mr-2" />Retour à l'accueil
          </Link>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}} data-testid="service-page-title">
            <span className="text-amber-400">{service.title}</span>
          </h1>
          <h2 className="text-xl sm:text-2xl text-amber-200/70 font-light mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            {service.subtitle}
          </h2>
          
          <p className="text-amber-100/70 text-lg leading-relaxed mb-8">{service.intro}</p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="tel:+41767836186" className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-6 py-3 rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all" data-testid="service-call-btn">
              <Phone className="w-5 h-5 mr-2" />+41 76 783 61 86
            </a>
            <a href="https://wa.me/41767836186" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-green-600 hover:bg-green-500 text-white font-bold px-6 py-3 rounded-lg transition-all" data-testid="service-whatsapp-btn">
              <MessageCircle className="w-5 h-5 mr-2" />WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 bg-neutral-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {service.sections.map((section, i) => (
              <article key={i} className="bg-gradient-to-b from-neutral-900 to-neutral-950 border border-amber-500/20 rounded-xl p-6 hover:border-amber-500/40 transition-all">
                <h3 className="text-xl font-bold text-amber-400 mb-3" style={{fontFamily: 'Cormorant Garamond, serif'}}>{section.title}</h3>
                <p className="text-amber-100/60 leading-relaxed">{section.content}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            <span className="text-amber-400">Témoignages</span> - {service.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {service.testimonials.map((t, i) => (
              <div key={i} className="bg-neutral-900/50 border border-amber-500/20 rounded-xl p-4">
                <div className="flex items-center space-x-1 mb-2">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />)}
                </div>
                <p className="text-amber-100/60 text-sm mb-3">"{t.text}"</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <span className="text-amber-400 text-xs font-bold">{t.name[0]}</span>
                    </div>
                    <div>
                      <span className="text-white text-sm font-medium">{t.name}</span>
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-3 h-3 text-amber-500" />
                        <span className="text-amber-200/50 text-xs">{t.city}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                    <span className="text-green-400/70 text-xs">Vérifié</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Keywords */}
      <section className="py-8 bg-neutral-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {service.keywords.map(kw => (
              <span key={kw} className="text-xs text-amber-200/50 bg-amber-500/5 px-3 py-1 rounded-full border border-amber-500/10">{kw}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-12 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Autres <span className="text-amber-400">Services</span> à Genève
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {otherServices.filter(s => s.slug !== slug).map(s => (
              <Link key={s.slug} to={`/service/${s.slug}`} className="text-center p-3 bg-neutral-900/50 border border-amber-500/20 rounded-lg hover:border-amber-500/40 hover:bg-amber-500/5 transition-all group">
                <span className="text-amber-200/70 text-sm group-hover:text-amber-400 transition-colors">{s.name}</span>
                <ArrowRight className="w-3 h-3 text-amber-500 mx-auto mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-neutral-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-amber-500/5 via-amber-500/10 to-amber-500/5 border border-amber-500/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              Consultation <span className="text-amber-400">Gratuite</span> - Marabout Genève
            </h2>
            <p className="text-amber-100/60 mb-6">Appelez maintenant pour une consultation gratuite et confidentielle. Résultats garantis en 3-7 jours.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+41767836186" className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-8 py-4 rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all">
                <Phone className="w-5 h-5 mr-2" />+41 76 783 61 86
              </a>
              <a href="https://wa.me/41767836186" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-green-600 hover:bg-green-500 text-white font-bold px-8 py-4 rounded-lg transition-all">
                <MessageCircle className="w-5 h-5 mr-2" />WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};
